/* eslint-disable import/no-extraneous-dependencies */
/* eslint-disable dot-notation */
import React, { useEffect, useMemo, useRef, useState } from 'react';

import Editor, { useMonaco } from '@monaco-editor/react';

import {
  Badge,
  Box,
  Button,
  CircularProgress,
  Grid,
  IconButton,
  InputAdornment,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import ToggleOffOutlinedIcon from '@mui/icons-material/ToggleOffOutlined';
import LockResetIcon from '@mui/icons-material/LockReset';
import ToggleOnIcon from '@mui/icons-material/ToggleOn';
import AddIcon from '@mui/icons-material/Add';
import { Code, ContentCopy, OpenInNew, PlayArrow, PlayCircle, TabRounded, Wysiwyg } from '@mui/icons-material';
import dracula from '../../../styles/themes/Dracula.json';
import FeedbackAlert, { FeedbackAlertType } from '../../../components/FeedbackAlert';
import api from '../../../services/api';

// type ViewType = 'dashboard' | 'requests' | 'attendance' | 'management' | 'report';

type QueryListType = {
  id: number;
  name: string;
};

type QueryType = {
  id: number;
  name: string;
  description: string;
  sql: string;
  created_at: string;
  created_by: number;
  created_by_name: string;
  modified_at: string;
  modified_by: number;
  modified_by_name: string;
  disabled_at: string;
  disabled_by: number;
  disabled_by_name: string;
  token: string;
};

interface TabPanelProps {
  children?: React.ReactNode;
  dir?: string;
  index: number;
  value: number;
}
function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`full-width-tabpanel-${index}`}
      aria-labelledby={`full-width-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          <Typography>{children}</Typography>
        </Box>
      )}
    </div>
  );
}

const handleCopy = async (textToCopy: string) => {
  try {
    await navigator.clipboard.writeText(textToCopy);
    return true;
  } catch (err) {
    return false;
  }
};

export default function HelpDeskHomePage() {
  const monacoEditorRef = useRef(null);
  const divInputBox = useRef(null);

  const [feedbackMsg, setFeedbackMsg] = useState('');
  const [feedbackType, setFeedbackType] = useState<FeedbackAlertType>('success');
  const [queryList, setQueryList] = useState<QueryListType[]>([] as QueryListType[]);
  const [query, setQuery] = useState<QueryType>(null);
  const [submiting, setSubmiting] = useState<boolean>(false);

  const [valueName, setValueName] = useState('');
  const [valueDescription, setValueDescription] = useState('');
  const [valueSql, setValueSql] = useState('');
  const [valueLink, setValueLink] = useState('');
  const [inputSearchValue, setInputSearchValue] = useState('');

  const [selectedTab, setSelectedTab] = React.useState(0);

  const [searchQuery, setSearchQuery] = useState('');

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setSelectedTab(newValue);
  };

  function handleEditorDidMount(editor, monaco) {
    monacoEditorRef.current = editor;
  }

  function handleEditorWillMount(monaco) {
    monaco.editor.defineTheme('dracula', dracula);
    monaco.editor.setTheme('dracula');
  }

  const getQueryList = async (search?: string) => {
    if (submiting) return;
    setSubmiting(true);

    try {
      const { data } = await api.get<QueryListType[]>(`it/querys/list`, {
        params: { basic: true, search },
      });
      setQueryList(data);
    } catch (error) {
      const { data: response } = error.response;
    }
    setSubmiting(false);
  };

  const getQuery = async (id: number) => {
    if (submiting) return;
    setSubmiting(true);
    try {
      const { data } = await api.get<QueryType[]>(`it/querys/list`, {
        params: { id },
      });

      setQuery(data?.[0]);
    } catch (error) {
      const { data: response } = error.response;
    }
    setSubmiting(false);
  };

  const insertQuery = async (name: string, description: string, sql: string) => {
    if (submiting) return;
    setFeedbackMsg('');
    setSubmiting(true);

    try {
      const { data } = await api.post('it/querys/add', {
        name,
        description,
        sql,
      });
      const response = data?.[0];
      if (response) {
        setFeedbackType('success');
        setFeedbackMsg('Query inserida com sucesso.');
        setQuery(response);
      } else {
        setFeedbackType('error');
        setFeedbackMsg(response?.error);
      }
    } catch (error) {
      const { data } = error.response;
      setFeedbackType('error');
      setFeedbackMsg(data?.error || data?.message);
    }
    setSubmiting(false);
  };
  const updateQuery = async (id: number, name: string, description: string, sql: string) => {
    if (submiting) return;
    setFeedbackMsg('');
    setSubmiting(true);

    try {
      const { data } = await api.post('it/querys/update', {
        id,
        name,
        description,
        sql,
      });
      const response = data?.[0];
      if (response) {
        setFeedbackType('success');
        setFeedbackMsg('Query salva com sucesso.');
        setQuery(response);
      } else {
        setFeedbackType('error');
        setFeedbackMsg(response?.error);
      }
    } catch (error) {
      const { data } = error.response;
      setFeedbackType('error');
      setFeedbackMsg(data?.error || data?.message);
    }
    setSubmiting(false);
  };

  const situationQuery = async (id: number, disable: boolean) => {
    if (submiting) return;
    setFeedbackMsg('');
    setSubmiting(true);

    try {
      const { data } = await api.post('it/querys/update', {
        id,
        situation: !disable ? 0 : 1,
      });
      const response = data?.[0];
      if (response) {
        setFeedbackType('success');
        setFeedbackMsg(`Query ${!disable ? 'desativada' : 'ativada'} com sucesso.'`);
        setQuery(response);
      } else {
        setFeedbackType('error');
        setFeedbackMsg(response?.message);
      }
    } catch (error) {
      const { data } = error.response;
      setFeedbackType('error');
      setFeedbackMsg(data?.error || data?.message);
    }
    setSubmiting(false);
  };

  const newTokenQuery = async (id: number) => {
    if (submiting) return;
    setFeedbackMsg('');
    setSubmiting(true);

    try {
      const { data } = await api.post('it/querys/update', {
        id,
        new_token: 1,
      });
      const response = data?.[0];
      if (response) {
        setFeedbackType('success');
        setFeedbackMsg('Novo token gerado com sucesso');
        setQuery(response);
      } else {
        setFeedbackType('error');
        setFeedbackMsg(response?.message);
      }
    } catch (error) {
      const { data } = error.response;
      setFeedbackType('error');
      setFeedbackMsg(data?.error || data?.message);
    }
    setSubmiting(false);
  };

  const onClickNewQuery = () => {
    setQuery(null);
  };

  useEffect(() => {
    const timerId = setTimeout(() => {
      setSearchQuery(inputSearchValue); // This triggers the actual search/API call
    }, 500);

    return () => {
      clearTimeout(timerId);
    };
  }, [inputSearchValue]);

  useEffect(() => {
    getQueryList(searchQuery);
  }, [searchQuery]);

  useEffect(() => {
    getQueryList();
  }, []);

  useEffect(() => {
    setValueName(query?.name || '');
    setValueDescription(query?.description || '');
    setValueSql(query?.sql || '');
    setValueLink(query ? `https://api.imic.com.br/it/querys/run?id=${query?.id}&token=${query?.token}` : '');
  }, [query]);

  const renderQueryList = useMemo(() => {
    if (!queryList || queryList.length === 0) {
      return <Typography>Nenhum item encontrado</Typography>;
    }

    return (
      <List
        sx={{
          overflow: 'auto',
          maxHeight: 'calc(100vh - 328px)',
          height: 'calc(100vh - 328px)',
        }}
      >
        {queryList.map(item => (
          <ListItem key={item.id} disablePadding>
            <ListItemButton
              component="a"
              onClick={() => getQuery(item.id)}
              selected={query?.id === item.id}
              disabled={submiting}
            >
              <Typography variant="subtitle1" noWrap overflow="hidden">
                {`${item.id} - ${item.name}`}
              </Typography>
            </ListItemButton>
          </ListItem>
        ))}
      </List>
    );
  }, [queryList, query, submiting, searchQuery]);

  return (
    <Box>
      <Grid container spacing={2}>
        <Grid item xs={12} sm={12} md={4} lg={3} xl={2}>
          <Box sx={{ display: 'flex', gap: 2, flexDirection: 'column', flex: 1, width: '100%' }}>
            <Box sx={{ display: 'flex', flex: 1, gap: 2 }}>
              <Button variant="contained" fullWidth color="success" size="large" onClick={onClickNewQuery}>
                NOVA QUERY
              </Button>
            </Box>
            <TextField
              size="small"
              id="search-querys"
              label="Pesquisar"
              variant="outlined"
              onChange={input => {
                setInputSearchValue(input.target.value);
              }}
            />
            <Box sx={{ width: '100%', bgcolor: 'background.paper', borderRadius: 1, p: 1 }}>{renderQueryList}</Box>
          </Box>
        </Grid>
        <Grid item xs={12} sm={12} md={8} lg={9} xl={10}>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
            {query &&
              (query?.disabled_by ? (
                <Button
                  variant="outlined"
                  color="warning"
                  disabled={submiting}
                  sx={{ width: 180 }}
                  onClick={() => situationQuery(query.id, true)}
                >
                  Ativar
                  {submiting ? (
                    <CircularProgress sx={{ ml: 2 }} size={16} color="inherit" />
                  ) : (
                    <ToggleOnIcon sx={{ ml: 2 }} fontSize="small" />
                  )}
                </Button>
              ) : (
                <Button
                  variant="outlined"
                  color="error"
                  disabled={submiting}
                  sx={{ width: 180 }}
                  onClick={() => situationQuery(query.id, false)}
                >
                  Desativar
                  {submiting ? (
                    <CircularProgress sx={{ ml: 2 }} size={16} color="inherit" />
                  ) : (
                    <ToggleOffOutlinedIcon sx={{ ml: 2 }} fontSize="small" />
                  )}
                </Button>
              ))}
            {query && (
              <Button
                variant="outlined"
                color="warning"
                disabled={submiting}
                sx={{ width: 180 }}
                onClick={() => newTokenQuery(query.id)}
              >
                Gerar Token
                {submiting ? (
                  <CircularProgress sx={{ ml: 2 }} size={16} color="inherit" />
                ) : (
                  <LockResetIcon sx={{ ml: 2 }} fontSize="small" />
                )}
              </Button>
            )}
            {query ? (
              <Button
                variant="outlined"
                color="success"
                onClick={() => updateQuery(query?.id, valueName, valueDescription, monacoEditorRef?.current.getValue())}
                disabled={submiting}
                sx={{ width: 180 }}
              >
                Salvar
                {submiting ? (
                  <CircularProgress sx={{ ml: 2 }} size={16} color="inherit" />
                ) : (
                  <SaveIcon sx={{ ml: 2 }} fontSize="small" />
                )}
              </Button>
            ) : (
              <Button
                variant="outlined"
                color="success"
                onClick={() => insertQuery(valueName, valueDescription, monacoEditorRef?.current.getValue())}
                disabled={submiting}
                sx={{ width: 180 }}
              >
                Salvar
                {submiting ? (
                  <CircularProgress sx={{ ml: 2 }} size={16} color="inherit" />
                ) : (
                  <AddIcon sx={{ ml: 2 }} fontSize="small" />
                )}
              </Button>
            )}
          </Box>
          <Tabs value={selectedTab} onChange={handleTabChange} aria-label="tabs">
            <Tab icon={<Wysiwyg />} iconPosition="start" label="Dados" id="full-width-tab-0" />
            <Tab icon={<Code />} iconPosition="start" label="SQL" id="full-width-tab-1" />
          </Tabs>
          <TabPanel value={selectedTab} index={0}>
            <Grid ref={divInputBox} container gap={2}>
              {query && (
                <Grid xs={12} sm={4} md={2} lg={1}>
                  <TextField
                    id="input-expected-end"
                    label="Código"
                    variant="outlined"
                    value={query?.id || ''}
                    disabled
                    InputProps={{
                      startAdornment: <InputAdornment position="start" />,
                    }}
                    size="small"
                    fullWidth
                  />
                </Grid>
              )}
              {query && (
                <Grid xs={12} sm={4} md={2} lg={1}>
                  <TextField
                    id="input-expected-end"
                    label="Situação"
                    variant="outlined"
                    value={query?.disabled_by ? 'Desativado' : 'Ativo'}
                    disabled
                    InputProps={{
                      startAdornment: <InputAdornment position="start" />,
                    }}
                    size="small"
                    fullWidth
                  />
                </Grid>
              )}
              {query && (
                <Grid xs={12} sm={4} md={2} lg={3}>
                  <TextField
                    id="input-expected-end"
                    label="Autor"
                    variant="outlined"
                    value={query?.created_by_name || ''}
                    disabled
                    InputProps={{
                      startAdornment: <InputAdornment position="start" />,
                    }}
                    size="small"
                    fullWidth
                  />
                </Grid>
              )}
              {query && (
                <Grid xs={12} sm={4} md={2} lg={2}>
                  <TextField
                    id="input-expected-end"
                    label="Data criação"
                    variant="outlined"
                    value={query?.created_at || ''}
                    disabled
                    InputProps={{
                      startAdornment: <InputAdornment position="start" />,
                    }}
                    size="small"
                    fullWidth
                  />
                </Grid>
              )}
              <Grid xs={12} sm={12} md={12} lg={12}>
                <TextField
                  id="input-expected-end"
                  label="Título"
                  variant="outlined"
                  value={valueName}
                  onChange={e => {
                    setValueName(e.target.value);
                  }}
                  InputProps={{
                    startAdornment: <InputAdornment position="start" />,
                  }}
                  size="small"
                  fullWidth
                />
              </Grid>
              <Grid xs={12} sm={12} md={12} lg={12}>
                <TextField
                  id="input-expected-end"
                  label="Descrição"
                  variant="outlined"
                  value={valueDescription}
                  multiline
                  minRows={3}
                  onChange={e => {
                    setValueDescription(e.target.value);
                  }}
                  InputProps={{
                    startAdornment: <InputAdornment position="start" />,
                  }}
                  size="small"
                  fullWidth
                />
              </Grid>

              {query && (
                <Grid xs={12} sm={4} md={2} lg={2}>
                  <TextField
                    id="input-expected-end"
                    label="Modificado por"
                    variant="outlined"
                    value={query?.modified_by ? query?.modified_by_name : ''}
                    disabled
                    InputProps={{
                      startAdornment: <InputAdornment position="start" />,
                    }}
                    size="small"
                    fullWidth
                  />
                </Grid>
              )}
              {query && (
                <Grid xs={12} sm={4} md={2} lg={2}>
                  <TextField
                    id="input-expected-end"
                    label="Data modificação"
                    variant="outlined"
                    value={query?.modified_at || ''}
                    disabled
                    InputProps={{
                      startAdornment: <InputAdornment position="start" />,
                    }}
                    size="small"
                    fullWidth
                  />
                </Grid>
              )}
              {query && (
                <Grid xs={12} sm={4} md={2} lg={2}>
                  <TextField
                    id="input-expected-end"
                    label="Responsável desativação"
                    variant="outlined"
                    value={query?.disabled_by_name || ''}
                    disabled
                    InputProps={{
                      startAdornment: <InputAdornment position="start" />,
                    }}
                    size="small"
                    fullWidth
                  />
                </Grid>
              )}
              {query && (
                <Grid xs={12} sm={4} md={2} lg={2}>
                  <TextField
                    id="input-expected-end"
                    label="Data desativação"
                    variant="outlined"
                    value={query?.disabled_at || ''}
                    disabled
                    InputProps={{
                      startAdornment: <InputAdornment position="start" />,
                    }}
                    size="small"
                    fullWidth
                  />
                </Grid>
              )}
              {query && (
                <Grid xs={12} sm={4} md={2} lg={8}>
                  <TextField
                    id="input-expected-end"
                    label="Link"
                    variant="outlined"
                    value={valueLink}
                    disabled
                    InputProps={{
                      startAdornment: <InputAdornment position="start" />,
                      endAdornment: (
                        <InputAdornment position="end">
                          <Box display="flex" gap={2}>
                            <Tooltip title="Abrir Link">
                              <IconButton
                                aria-label="Abrir"
                                href={valueLink}
                                target="_blank"
                                rel="noopener noreferrer"
                                edge="end"
                              >
                                <OpenInNew />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Copiar Texto">
                              <IconButton aria-label="Copiar" edge="end" onClick={() => handleCopy(valueLink)}>
                                <ContentCopy />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </InputAdornment>
                      ),
                    }}
                    size="small"
                    fullWidth
                  />
                </Grid>
              )}
              {query && (
                <Grid xs={12} sm={4} md={2} lg={3}>
                  <TextField
                    id="input-expected-end"
                    label="Token"
                    variant="outlined"
                    value={query?.token}
                    disabled
                    InputProps={{
                      startAdornment: <InputAdornment position="start" />,
                    }}
                    size="small"
                    fullWidth
                  />
                </Grid>
              )}
            </Grid>
          </TabPanel>
          <TabPanel value={selectedTab} index={1}>
            <Box>
              <Editor
                height="calc(100vh - 330px)"
                defaultLanguage="sql"
                theme="dracula"
                value={query?.sql || ''}
                onMount={handleEditorDidMount}
                beforeMount={handleEditorWillMount}
                loading={submiting}
              />
            </Box>
          </TabPanel>
        </Grid>
      </Grid>

      <FeedbackAlert message={feedbackMsg} show={feedbackMsg?.length > 0} type={feedbackType} />
    </Box>
  );
}
